// apps/admin/src/pages/Permissions.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield,
  Search,
  CheckCircle,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Users,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AdminAuthContext';
import pb from '../lib/pocketbaseClient';

// ---- All apps and their pages ----
const APP_PAGES = {
  accounts: {
    label: 'Accounts',
    pages: [
      'MainDashboard',
      'ConstructionDashboard',
      'InteriorDashboard',
      'Projects',
      'ProjectDetail',
      'Partners',
      'Vendors',
      'ProjectExpenses',
      'VendorPayments',
      'ClientPayments',
      'Investments',
      'Reports',
    ],
  },
  'file-manager': {
    label: 'File Manager',
    pages: [
      'Dashboard',
      'Portfolio',
      'Products',
      'EBooks',
      'Videos',
      'Calculators',
      'Gallery',
      'Properties',
      'Customers',
      'Settings',
      'Blogs',
    ],
  },
  hr: {
    label: 'HR',
    pages: [
      'Dashboard',
      'Employees',
      'Attendance',
      'Leave',
      'Salary',
      'Promotions',
      'Expenses',
      'Reports',
    ],
  },
  office: {
    label: 'Office',
    pages: [
      'Dashboard',
      'Clients',
      'Quotations',
      'Invoices',
      'PaymentsDashboard',
      'Tasks',
      'WhatsApp',
      'Properties',
      'AbandonedCheckouts',
      'Reports',
      'PriceList',
      'PlanningPricePage',
      'Approvals',
      'CartOverview',
    ],
  },
};

// Flatten all pages
const ALL_PAGES = Object.keys(APP_PAGES).reduce((acc, app) => {
  APP_PAGES[app].pages.forEach((page) => {
    acc.push({ app, page });
  });
  return acc;
}, []);

const Permissions = () => {
  const { role } = useAuth();

  // State
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedApps, setExpandedApps] = useState({});
  const [initialPermissions, setInitialPermissions] = useState({});

  // ---- Helper Functions ----
  const fetchTeamMembers = async () => {
    try {
      const users = await pb.collection('users').getFullList({
        filter: 'role != "user" && role != "customer"',
        sort: 'name',
      });
      setTeamMembers(users);
      if (users.length > 0) {
        setSelectedUser(users[0]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const fetchPermissionsForUser = async (userId) => {
    if (!userId) return;
    setLoading(true);
    try {
      // Check if 'permissions' collection exists (just try to get any record)
      let records = [];
      try {
        records = await pb.collection('permissions').getFullList({
          filter: `user = "${userId}"`,
        });
      } catch (err) {
        if (err.message?.includes('collection')) {
          toast.error(
            'Permissions collection not found. Please create it in PocketBase.'
          );
        } else {
          throw err;
        }
        setLoading(false);
        return;
      }

      // Build default permissions object (all true)
      const permObj = {};
      ALL_PAGES.forEach(({ app, page }) => {
        if (!permObj[app]) permObj[app] = {};
        permObj[app][page] = true;
      });

      // Override with stored permissions
      records.forEach((rec) => {
        if (permObj[rec.app] && permObj[rec.app][rec.page] !== undefined) {
          permObj[rec.app][rec.page] = rec.allowed;
        }
      });

      setPermissions(permObj);
      setInitialPermissions(JSON.parse(JSON.stringify(permObj)));
      setExpandedApps(
        Object.keys(APP_PAGES).reduce((acc, app) => ({ ...acc, [app]: true }), {})
      );
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const hasAppChanges = (app) => {
    if (!initialPermissions[app]) return false;
    return APP_PAGES[app].pages.some(
      (page) =>
        (permissions[app]?.[page] ?? true) !==
        (initialPermissions[app]?.[page] ?? true)
    );
  };

  const togglePermission = (app, page) => {
    setPermissions((prev) => {
      const currentValue = prev[app]?.[page] ?? true;
      return {
        ...prev,
        [app]: {
          ...(prev[app] || {}),
          [page]: !currentValue,
        },
      };
    });
  };

  const toggleAllPages = (app, value) => {
    const appPages = APP_PAGES[app].pages;
    setPermissions((prev) => {
      const newPerms = { ...prev };
      if (!newPerms[app]) newPerms[app] = {};
      appPages.forEach((page) => {
        newPerms[app][page] = value;
      });
      return newPerms;
    });
  };

  const savePermissions = async () => {
    if (!selectedUser) {
      toast.error('No user selected');
      return;
    }

    // Double-check that collection exists before attempting save
    try {
      await pb.collection('permissions').getList(1, 1, { limit: 1 });
    } catch (err) {
      toast.error('Permissions collection not found. Please create it in PocketBase.');
      console.error(err);
      return;
    }

    setSaving(true);
    try {
      // 1. Delete all existing permissions for this user
      const existing = await pb.collection('permissions').getFullList({
        filter: `user = "${selectedUser.id}"`,
      });
      for (const rec of existing) {
        await pb.collection('permissions').delete(rec.id);
      }

      // 2. Prepare new permission records
      const records = ALL_PAGES.map(({ app, page }) => ({
        user: selectedUser.id,
        app,
        page,
        allowed: permissions[app]?.[page] !== undefined ? permissions[app][page] : true,
      }));

      // 3. Create new records
      for (const rec of records) {
        await pb.collection('permissions').create(rec);
      }

      toast.success('✅ Permissions saved successfully!');
      // Update initial state to match saved state
      setInitialPermissions(JSON.parse(JSON.stringify(permissions)));
    } catch (error) {
      console.error('Save error:', error);
      let errorMsg = 'Failed to save permissions. ';
      if (error.message?.includes('permission')) {
        errorMsg += 'You may not have write access to the permissions collection.';
      } else if (error.message?.includes('invalid field')) {
        errorMsg += 'Check that the collection fields match: user, app, page, allowed.';
      } else {
        errorMsg += error.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const resetPermissions = () => {
    setPermissions(JSON.parse(JSON.stringify(initialPermissions)));
    toast.info('Reset to last saved state');
  };

  // ---- React Hooks ----
  useEffect(() => {
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchPermissionsForUser(selectedUser.id);
    }
  }, [selectedUser]);

  const hasChanges = useMemo(() => {
    return Object.keys(APP_PAGES).some((app) => hasAppChanges(app));
  }, [permissions, initialPermissions]);

  // ---- Role check ----
  if (role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  // ---- Filtered members ----
  const filteredMembers = teamMembers.filter(
    (m) =>
      m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ---- Render ----
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Team Permissions</h1>
          <p className="text-sm text-slate-500">
            Manage access for each team member across all applications.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <button
              onClick={resetPermissions}
              className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
            >
              Reset
            </button>
          )}
          <button
            onClick={savePermissions}
            disabled={saving || !hasChanges}
            className={`px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
              saving || !hasChanges
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
            }`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Team Member List */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-slate-200 p-4 h-fit">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
          </div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {filteredMembers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No members found</p>
            ) : (
              filteredMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => setSelectedUser(member)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition flex items-center gap-3 ${
                    selectedUser?.id === member.id
                      ? 'bg-indigo-50 border border-indigo-200'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">
                    {member.name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {member.name}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{member.email}</p>
                  </div>
                  {selectedUser?.id === member.id && (
                    <CheckCircle className="w-4 h-4 text-indigo-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: Permissions Matrix */}
        <div className="lg:col-span-3">
          {selectedUser ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-semibold">
                    {selectedUser.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {selectedUser.name}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {selectedUser.email} • {selectedUser.role}
                    </p>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 flex justify-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(APP_PAGES).map((appKey) => {
                    const app = APP_PAGES[appKey];
                    const isExpanded = expandedApps[appKey] !== false;
                    const appPerms = permissions[appKey] || {};
                    const allChecked = app.pages.every((p) => appPerms[p] === true);
                    const appChanged = hasAppChanges(appKey);

                    return (
                      <div
                        key={appKey}
                        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                      >
                        <button
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
                          onClick={() =>
                            setExpandedApps((prev) => ({
                              ...prev,
                              [appKey]: !prev[appKey],
                            }))
                          }
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-slate-700">
                              {app.label}
                            </span>
                            {appChanged && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                Unsaved
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllPages(appKey, !allChecked);
                              }}
                              className={`text-xs px-2 py-1 rounded border ${
                                allChecked
                                  ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                                  : 'bg-slate-100 text-slate-600 border-slate-300'
                              }`}
                            >
                              {allChecked ? 'Deselect All' : 'Select All'}
                            </button>
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="border-t border-slate-100 px-4 py-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {app.pages.map((page) => (
                              <label
                                key={page}
                                className="flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 p-1.5 rounded transition cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={(appPerms[page] ?? true) === true}
                                  onChange={() => togglePermission(appKey, page)}
                                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="truncate">{page}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-700">
                Select a team member
              </h3>
              <p className="text-sm text-slate-500">
                Choose a member from the left panel to manage their permissions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Permissions;